import { getSessionExpiredLoginUrl } from '@/lib/loginPath';
import { siteMasterLoginUrl } from '@/lib/siteMasterPaths';
import { dispatchMediaQuotaBlocked, type MediaStorageStatus } from '@/lib/mediaStorage';

const ASSETS_MARKER = '/assets/';

/** 从打包后本文件所在 chunk 的 URL 推断部署根（与 dist/api 同级）。比扫 document.scripts 更稳（拆包、顺序、内联脚本不影响）。 */
function inferredAdminRootFromImportMeta(): string {
  try {
    const u = new URL(import.meta.url);
    const p = u.pathname;
    const j = p.indexOf(ASSETS_MARKER);
    if (j <= 0) {
      return '';
    }
    return p.slice(0, j).replace(/\/+$/, '') || '';
  } catch {
    return '';
  }
}

/**
 * 从当前页面 module 脚本 src 推断部署根（兜底；要求路径含「前缀 + /assets/…」）。
 * 若 index.html 写死为站点根 `/assets/index.js`，此处无法推出子目录，请设 window.__WES_API_BASE__。
 */
function inferredAdminRootFromModuleScript(): string {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return '';
  }
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i--) {
    const el = scripts.item(i) as HTMLScriptElement | null;
    if (!el || el.type !== 'module') {
      continue;
    }
    const src = el.src;
    if (!src) {
      continue;
    }
    try {
      const u = new URL(src, window.location.href);
      const p = u.pathname;
      const j = p.indexOf(ASSETS_MARKER);
      if (j > 0) {
        return p.slice(0, j).replace(/\/+$/, '') || '';
      }
    } catch {
      /* ignore */
    }
  }
  return '';
}

function base(): string {
  if (typeof window !== 'undefined' && (window as unknown as { __WES_API_BASE__?: string }).__WES_API_BASE__) {
    return (window as unknown as { __WES_API_BASE__: string }).__WES_API_BASE__.replace(/\/$/, '');
  }
  const fromMeta = inferredAdminRootFromImportMeta();
  if (fromMeta !== '') {
    return fromMeta;
  }
  const inferred = inferredAdminRootFromModuleScript();
  if (inferred !== '') {
    return inferred;
  }
  const viteBase = (import.meta.env.BASE_URL ?? '/').replace(/\/+$/, '');
  if (viteBase !== '' && viteBase !== '/') {
    return viteBase;
  }
  return '';
}

export function resolveMediaUrl(localPath: string): string {
  const p = localPath.trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  const path = p.startsWith('/') ? p : `/${p}`;
  const apiBase = base();
  if (apiBase) {
    return `${apiBase}${path}`;
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return path;
}

export function apiUrl(action: string, query: Record<string, string | number | undefined> = {}): string {
  const q = new URLSearchParams();
  q.set('action', action);
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  return `${base()}/api/api.php?${q.toString()}`;
}

export async function apiFetch<T>(
  action: string,
  opts: RequestInit & {
    query?: Record<string, string | number | undefined>;
    /**
     * 默认：POST/PUT/PATCH 且 body 为字符串时，使用 text/plain 发 JSON，规避部分机房 WAF 对 application/json 的误拦。
     * 若某接口必须带 application/json，可设 contentTypeJson: true。
     */
    contentTypeJson?: boolean;
  } = {}
): Promise<T> {
  const { query, contentTypeJson, ...init } = opts;
  const url = apiUrl(action, query ?? {});
  const method = (init.method ?? 'GET').toString().toUpperCase();
  const requestBody = init.body;
  const isForm = typeof FormData !== 'undefined' && requestBody instanceof FormData;
  const isJsonString =
    typeof requestBody === 'string' && (method === 'POST' || method === 'PUT' || method === 'PATCH');

  const headers = new Headers(init.headers as HeadersInit | undefined);
  if (!headers.has('Content-Type')) {
    if (isForm) {
      /* 由浏览器自动带 multipart boundary */
    } else if (isJsonString) {
      headers.set(
        'Content-Type',
        contentTypeJson === true ? 'application/json' : 'text/plain;charset=UTF-8',
      );
    }
  }
  if (isForm) {
    headers.delete('Content-Type');
  }

  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    headers,
  });
  const raw = await res.text();
  const text = raw.replace(/^\uFEFF/, '').trim();
  if (text === '') {
    throw new Error(`接口返回空响应（HTTP ${res.status}）`);
  }
  const looksJson = text.startsWith('{') || text.startsWith('[');
  if (!looksJson) {
    const snippet = text.replace(/\s+/g, ' ').slice(0, 280);
    const hint404 =
      res.status === 404 && text.includes('<!DOCTYPE')
        ? ' 若同域 POST login/logout 仍返回 JSON，而仅个别 action 得到整页 HTML，多为宝塔/WAF 按 URL 关键词误拦（如含 interface 的 action）；可在面板放行或改用更窄的 action。若所有 POST 均如此，再查 Nginx：location / 的 try_files 是否把 POST 交给 index.html（见 deploy/baota-extension-wes-00-api-api-php.conf、nginx-wes-site.conf.example）。'
        : '';
    throw new Error(
      `接口未返回 JSON（HTTP ${res.status}）。请求：${url.split('?')[0]}${hint404}${snippet ? ` 响应片段：${snippet}` : ''}`
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`接口 JSON 解析失败（HTTP ${res.status}）`);
  }

  const respBody = parsed as {
    ok?: boolean;
    err?: string;
    media_storage?: MediaStorageStatus;
  };
  if (
    typeof window !== 'undefined' &&
    respBody &&
    respBody.ok === false &&
    respBody.err === 'media_quota_exceeded'
  ) {
    dispatchMediaQuotaBlocked(respBody.media_storage);
  }
  if (
    typeof window !== 'undefined' &&
    respBody &&
    respBody.ok === false &&
    respBody.err === 'unauthorized'
  ) {
    const path = window.location.pathname;
    const onLoginScreen =
      path === '/login' ||
      path === '/m/login' ||
      path === '/w/login' ||
      path.startsWith('/login/') ||
      path.startsWith('/m/login/') ||
      path.startsWith('/w/login');
    if (!onLoginScreen) {
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (path.startsWith('/w/')) {
        window.location.replace(siteMasterLoginUrl(returnTo));
      } else {
        window.location.replace(getSessionExpiredLoginUrl(returnTo));
      }
    }
    throw new Error('SESSION_EXPIRED');
  }

  return parsed as T;
}
